import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Brain, Zap, AlertTriangle, CheckCircle2, TrendingUp } from 'lucide-react';
import { API_URL } from '@/services/apiClient';

interface AIDetectionData {
  total: number;
  alertGenerated: number;
  suppressed: number;
  highConfidence: number;
  dismissed: number;
  falsePositiveRate: number;
  byType: Array<{ type: string; count: number; avgConfidence: number }>;
  monthlyTrend: Array<{ month: string; count: number; flagged: number }>;
}

const DETECTION_COLORS: Record<string, string> = {
  cyberbullying: 'bg-purple-500',
  self_harm: 'bg-red-500',
  violence: 'bg-orange-500',
  profanity: 'bg-blue-500',
  hate_speech: 'bg-pink-500',
  threat: 'bg-amber-500',
  none: 'bg-slate-400',
};

const DETECTION_TEXT: Record<string, string> = {
  cyberbullying: 'text-purple-500',
  self_harm: 'text-red-500',
  violence: 'text-orange-500',
  profanity: 'text-blue-500',
  hate_speech: 'text-pink-500',
  threat: 'text-amber-500',
  none: 'text-slate-400',
};

export function AIDetectionPanel({ dm }: { dm: boolean }) {
  const [data, setData] = useState<AIDetectionData | null>(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(30);

  const cardCls = `border shadow-sm ${dm ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}`;
  const titleCls = dm ? 'text-slate-100' : 'text-slate-800';
  const descCls = dm ? 'text-slate-400' : 'text-slate-500';
  const mutedCls = dm ? 'text-slate-500' : 'text-slate-400';

  const fetch_ = useCallback(async () => {
    setLoading(true);
    const token = sessionStorage.getItem('authToken');
    try {
      const res = await fetch(`${API_URL}/dashboard/school-admin/ai-detection?days=${days}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) setData(await res.json());
    } catch {/* */} finally { setLoading(false); }
  }, [days]);

  useEffect(() => { fetch_(); }, [fetch_]);

  const maxTypeCount = Math.max(...(data?.byType.map(t => t.count) ?? [1]), 1);
  const maxMonthTotal = Math.max(...(data?.monthlyTrend.map(m => m.count) ?? [1]), 1);

  return (
    <div className="space-y-5" data-dm={dm}>
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h2 className={`text-lg font-semibold ${titleCls}`}>AI Detection Activity</h2>
        <div className="flex gap-2">
          {[7, 30, 90].map(d => (
            <button key={d} onClick={() => setDays(d)}
              className={`btn-glass btn-glass-pill text-sm ${days === d ? 'btn-glass-blue' : ''}`}>{d}d</button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" />
        </div>
      ) : (
        <>
          {/* KPIs */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
            {[
              { label: 'Total Scans', value: data?.total ?? 0, icon: Brain, color: 'blue' },
              { label: 'Alerts Generated', value: data?.alertGenerated ?? 0, icon: AlertTriangle, color: 'red' },
              { label: 'Suppressed', value: data?.suppressed ?? 0, icon: CheckCircle2, color: 'emerald' },
              { label: 'High Confidence', value: data?.highConfidence ?? 0, icon: Zap, color: 'amber' },
              { label: 'False Positive %', value: `${data?.falsePositiveRate ?? 0}%`, icon: TrendingUp, color: 'purple' },
            ].map((s, i) => (
              <Card key={i} className={cardCls}>
                <CardContent className="pt-4 pb-4">
                  <div className={`h-8 w-8 rounded-lg flex items-center justify-center mb-2 ${dm ? `bg-${s.color}-900/50` : `bg-${s.color}-100`}`}>
                    <s.icon className={`h-4 w-4 text-${s.color}-500`} />
                  </div>
                  <p className={`text-xl font-bold ${titleCls}`}>{typeof s.value === 'number' ? s.value.toLocaleString() : s.value}</p>
                  <p className={`text-xs ${descCls}`}>{s.label}</p>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            {/* By detection type */}
            <Card className={cardCls}>
              <CardHeader className="pb-3">
                <CardTitle className={`text-base ${titleCls}`}>Detections by Type</CardTitle>
                <CardDescription className={descCls}>What the ML pipeline is flagging</CardDescription>
              </CardHeader>
              <CardContent>
                {data?.byType.length ? (
                  <div className="space-y-3">
                    {data.byType.sort((a, b) => b.count - a.count).map((t, i) => (
                      <div key={i} className="flex items-center gap-3">
                        <span className={`text-xs w-24 shrink-0 capitalize ${DETECTION_TEXT[t.type] || mutedCls}`}>
                          {t.type.replace(/_/g, ' ')}
                        </span>
                        <div className={`flex-1 h-2.5 rounded-full overflow-hidden ${dm ? 'bg-slate-700' : 'bg-slate-100'}`}>
                          <div className={`h-full rounded-full transition-all duration-500 ${DETECTION_COLORS[t.type] || 'bg-slate-400'}`}
                            style={{ width: `${Math.round((t.count / maxTypeCount) * 100)}%` }} />
                        </div>
                        <span className={`text-xs font-semibold w-6 text-right shrink-0 ${dm ? 'text-slate-300' : 'text-slate-700'}`}>{t.count}</span>
                        <span className={`text-xs w-12 text-right shrink-0 ${mutedCls}`}>
                          {Math.round(t.avgConfidence * 100)}% conf
                        </span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className={`flex flex-col items-center justify-center h-32 ${mutedCls}`}>
                    <Brain className="h-8 w-8 mb-2 opacity-40" />
                    <p className="text-sm">No detection data yet</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Monthly trend */}
            <Card className={cardCls}>
              <CardHeader className="pb-3">
                <CardTitle className={`text-base ${titleCls}`}>Monthly Detection Trend</CardTitle>
                <CardDescription className={descCls}>Total scans vs alerts generated</CardDescription>
              </CardHeader>
              <CardContent>
                {data?.monthlyTrend.some(m => m.count > 0) ? (
                  <div className="space-y-3">
                    {data.monthlyTrend.map((m, i) => {
                      const flaggedPct = m.count > 0 ? Math.round((m.flagged / m.count) * 100) : 0;
                      const barPct = Math.round((m.count / maxMonthTotal) * 100);
                      return (
                        <div key={i} className="flex items-center gap-3">
                          <span className={`text-xs w-8 shrink-0 ${mutedCls}`}>{m.month}</span>
                          <div className={`flex-1 h-4 rounded-full overflow-hidden ${dm ? 'bg-slate-700' : 'bg-slate-100'}`}>
                            <div className="h-full flex">
                              <div className="h-full bg-blue-500 transition-all duration-500"
                                style={{ width: `${barPct - flaggedPct > 0 ? barPct - flaggedPct : 0}%` }} />
                              <div className="h-full bg-red-500 transition-all duration-500"
                                style={{ width: `${Math.min(flaggedPct, barPct)}%` }} />
                            </div>
                          </div>
                          <span className={`text-xs w-10 text-right shrink-0 ${mutedCls}`}>{m.count}</span>
                        </div>
                      );
                    })}
                    <div className="flex items-center gap-4 pt-1">
                      <div className="flex items-center gap-1.5">
                        <div className="h-2.5 w-2.5 rounded-sm bg-blue-500" />
                        <span className={`text-xs ${mutedCls}`}>Scans</span>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <div className="h-2.5 w-2.5 rounded-sm bg-red-500" />
                        <span className={`text-xs ${mutedCls}`}>Alerts</span>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className={`flex flex-col items-center justify-center h-32 ${mutedCls}`}>
                    <TrendingUp className="h-8 w-8 mb-2 opacity-40" />
                    <p className="text-sm">No trend data yet</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </>
      )}
    </div>
  );
}
